import React, { useState } from 'react'
import { Revenue, CreatorEarnings } from '../../types'
import RevenueChart from './RevenueChart'
import RevenueOverview from './RevenueOverview'
import Tooltip from '../Tooltip'
import { MONETIZATION_GLOSSARY } from '../../config/glossary'
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline'

/** Percent of `total` that `part` makes up, or null when there is no total to divide by. */
const shareOf = (part: number, total: number): number | null =>
  total > 0 && Number.isFinite(part) ? (part / total) * 100 : null

/** "75%", or "—" when there is no revenue to split. */
const formatShare = (pct: number | null): string => (pct == null ? '—' : `${pct.toFixed(0)}%`)

interface RevenueAnalyticsProps {
  earnings: CreatorEarnings
  revenueHistory: Revenue[]
  isLoading?: boolean
}

const RevenueAnalytics: React.FC<RevenueAnalyticsProps> = ({
  earnings,
  revenueHistory,
  isLoading = false,
}) => {
  const [timePeriod, setTimePeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d')

  const getFilteredData = () => {
    const now = new Date()
    const cutoffDate = new Date()

    switch (timePeriod) {
      case '7d':
        cutoffDate.setDate(now.getDate() - 7)
        break
      case '30d':
        cutoffDate.setDate(now.getDate() - 30)
        break
      case '90d':
        cutoffDate.setDate(now.getDate() - 90)
        break
      case '1y':
        cutoffDate.setFullYear(now.getFullYear() - 1)
        break
    }

    return revenueHistory.filter((rev) => new Date(rev.period) >= cutoffDate)
  }

  const calculateGrowth = (data: Revenue[]) => {
    if (data.length < 2) return { value: 0, isPositive: true }

    const latest = data[data.length - 1].total_revenue
    const previous = data[data.length - 2].total_revenue

    if (previous === 0) return { value: 0, isPositive: true }

    const growth = ((latest - previous) / previous) * 100
    return {
      value: Math.abs(growth),
      isPositive: growth >= 0,
    }
  }

  const filteredData = getFilteredData()
  const growth = calculateGrowth(filteredData)

  const subscriberGrowth = filteredData.length >= 2
    ? filteredData[filteredData.length - 1].new_subscribers -
      filteredData[filteredData.length - 1].cancelled_subscribers
    : 0

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Revenue Analytics</h2>
          <p className="text-sm text-gray-600 mt-1">
            Track your earnings and subscriber growth
          </p>
        </div>

        <div className="inline-flex rounded-md shadow-sm">
          {(['7d', '30d', '90d', '1y'] as const).map((period) => (
            <button
              key={period}
              onClick={() => setTimePeriod(period)}
              className={`px-4 py-2 text-sm font-medium ${
                period === '7d' ? 'rounded-l-md' : period === '1y' ? 'rounded-r-md' : ''
              } ${
                timePeriod === period
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } border ${period !== '7d' ? 'border-l-0' : ''}`}
            >
              {period === '7d'
                ? '7 Days'
                : period === '30d'
                ? '30 Days'
                : period === '90d'
                ? '90 Days'
                : '1 Year'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">
            <Tooltip content={MONETIZATION_GLOSSARY.periodRevenue}>Period Revenue</Tooltip>
          </h3>
          <p className="text-3xl font-bold text-gray-900">
            $
            {filteredData
              .reduce((sum, rev) => sum + rev.total_revenue, 0)
              .toFixed(2)}
          </p>
          <div className="flex items-center mt-2">
            {growth.isPositive ? (
              <ArrowUpIcon className="h-4 w-4 text-green-600 mr-1" />
            ) : (
              <ArrowDownIcon className="h-4 w-4 text-red-600 mr-1" />
            )}
            <span
              className={`text-sm font-medium ${
                growth.isPositive ? 'text-green-600' : 'text-red-600'
              }`}
            >
              {growth.value.toFixed(1)}%
            </span>
            <span className="text-sm text-gray-500 ml-1">vs previous period</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Subscriber Growth</h3>
          <p className="text-3xl font-bold text-gray-900">
            {subscriberGrowth >= 0 ? '+' : ''}
            {subscriberGrowth}
          </p>
          <div className="flex items-center mt-2">
            <span className="text-sm text-gray-600">
              New:{' '}
              <span className="font-medium text-green-600">
                +
                {filteredData.length > 0
                  ? filteredData[filteredData.length - 1].new_subscribers
                  : 0}
              </span>
              , Cancelled:{' '}
              <span className="font-medium text-red-600">
                -
                {filteredData.length > 0
                  ? filteredData[filteredData.length - 1].cancelled_subscribers
                  : 0}
              </span>
            </span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Average Revenue</h3>
          <p className="text-3xl font-bold text-gray-900">
            $
            {filteredData.length > 0
              ? (
                  filteredData.reduce((sum, rev) => sum + rev.total_revenue, 0) /
                  filteredData.length
                ).toFixed(2)
              : '0.00'}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Per {timePeriod === '7d' || timePeriod === '30d' ? 'day' : 'period'}
          </p>
        </div>
      </div>

      <RevenueOverview earnings={earnings} isLoading={isLoading} />

      <RevenueChart data={filteredData} isLoading={isLoading} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Breakdown</h3>
          {filteredData.length > 0 ? (
            <div className="space-y-4">
              {(() => {
                const total = filteredData.reduce((sum, rev) => sum + rev.total_revenue, 0)
                const subscription = filteredData.reduce(
                  (sum, rev) => sum + rev.subscription_revenue,
                  0
                )
                const tip = filteredData.reduce((sum, rev) => sum + rev.tip_revenue, 0)
                // With no revenue in the period, part / total is 0 / 0 = NaN.
                const subscriptionShare = shareOf(subscription, total)
                const tipShare = shareOf(tip, total)

                return (
                  <>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          Subscription Revenue
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          ${subscription.toFixed(2)} ({formatShare(subscriptionShare)})
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${subscriptionShare ?? 0}%` }}
                        ></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">
                          <Tooltip content={MONETIZATION_GLOSSARY.tipRevenue}>Tip Revenue</Tooltip>
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          ${tip.toFixed(2)} ({formatShare(tipShare)})
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full"
                          style={{ width: `${tipShare ?? 0}%` }}
                        ></div>
                      </div>
                    </div>
                  </>
                )
              })()}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No revenue data available</p>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Subscriber Metrics</h3>
          {filteredData.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Subscribers</span>
                <span className="text-lg font-bold text-gray-900">
                  {filteredData[filteredData.length - 1].subscriber_count}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">New Subscribers</span>
                <span className="text-lg font-bold text-green-600">
                  +{filteredData.reduce((sum, rev) => sum + rev.new_subscribers, 0)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Cancelled Subscriptions</span>
                <span className="text-lg font-bold text-red-600">
                  -{filteredData.reduce((sum, rev) => sum + rev.cancelled_subscribers, 0)}
                </span>
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-700">
                  <Tooltip content={MONETIZATION_GLOSSARY.netGrowth}>Net Growth</Tooltip>
                </span>
                <span className="text-lg font-bold text-blue-600">
                  {filteredData.reduce(
                    (sum, rev) => sum + rev.new_subscribers - rev.cancelled_subscribers,
                    0
                  )}
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No subscriber data available</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default RevenueAnalytics
